// src/contexts/CartContext.jsx
import { createContext, useContext, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useQuery as useApolloQuery, useMutation } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import FETCH_CART_DETAILS from '../api/queries/FetchCartDetails.gql';
import CREATE_CHECKOUT from '../api/queries/CreateCheckout.gql';
import { getExistingCheckout, consoleDebug } from '../util';
import * as storage from '../browserStorage';
import { storageItemName } from '../config';
import { useConfig } from './ConfigContext';
import { useAppState } from './AppStateContext';
import { isSweedTestMode } from '../sweedConfig';

const CartContext = createContext(null);

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const { retailerId } = useAppState();
    const { retailers } = useConfig();
    const location = useLocation();

    // TEST HARNESS: Sweed has no cart/checkout API yet. Keep the cart inert (no
    // Dutchie worker calls) so it doesn't block product loading. cartData stays null
    // and isFetched stays true, which satisfies consumers' "cart ready" checks.
    const sweedTest = isSweedTestMode();

    // Get retailer data from WordPress config
    const retailerData = retailers?.[retailerId] || {};

    // Track the current checkout data
    const [checkoutData, setCheckoutData] = useState(() => getExistingCheckout());

    // Track if we've already attempted to fetch to prevent duplicate queries
    const fetchAttemptedRef = useRef(false);
    const lastFetchParamsRef = useRef(null);

    // Check if we're on order confirmation page
    const isOrderConfirmationPage = useMemo(() => {
        return location.pathname.includes('/order-confirmation') ||
            location.search.includes('orderNumber=');
    }, [location]);

    // Create cart mutation
    const [createCheckoutMutation, { loading: isCreatingCart }] = useMutation(CREATE_CHECKOUT, {
        onCompleted: (data) => {
            const checkout = data.createCheckout;

            // Store in localStorage
            const newCheckoutData = {
                checkoutId: checkout.id,
                retailerId: retailerId,
                createdAt: Date.now(),
            };

            storage.setItem(storageItemName, JSON.stringify(newCheckoutData));

            // Update local state
            setCheckoutData(newCheckoutData);

            // Reset fetch flags so the new cart will be fetched
            fetchAttemptedRef.current = false;
            lastFetchParamsRef.current = null;

            consoleDebug('✅ Cart created:', {
                checkoutId: checkout.id,
                retailerId,
                pricingType: checkout.pricingType
            });
        },
        onError: (error) => {
            console.error('Create cart error:', error);
        }
    });

    // Create cart function - now respects retailer's menuType
    const createCart = useCallback(async (orderType = 'PICKUP', overridePricingType = null) => {
        if (!retailerId) {
            console.error('Cannot create cart without retailerId');
            return;
        }

        // Don't create cart on order confirmation page
        if (isOrderConfirmationPage) {
            consoleDebug('Skipping cart creation on order confirmation page');
            return;
        }

        // Determine pricing type based on retailer's menuType
        let pricingType = 'RECREATIONAL'; // default

        if (overridePricingType) {
            // If explicitly provided, use that
            pricingType = overridePricingType;
            consoleDebug('Using override pricing type:', pricingType);
        } else if (retailerData?.menuType) {
            // Check retailer's menuType setting
            const menuType = retailerData.menuType.toLowerCase();
            if (menuType === 'medical') {
                pricingType = 'MEDICAL';
            } else if (menuType === 'recreational') {
                pricingType = 'RECREATIONAL';
            }
            // Note: some retailers might have 'both' or other values - default to RECREATIONAL for those
            consoleDebug('Using retailer menuType:', {
                menuType: retailerData.menuType,
                resultingPricingType: pricingType
            });
        } else {
            consoleDebug('No retailer menuType found, using default RECREATIONAL');
        }

        consoleDebug('Creating new cart:', {
            retailerId,
            orderType,
            pricingType,
            retailerMenuType: retailerData?.menuType,
            hasRetailerData: !!retailerData
        });

        return createCheckoutMutation({
            variables: {
                retailerId,
                orderType,
                pricingType
            }
        });
    }, [retailerId, createCheckoutMutation, isOrderConfirmationPage, retailerData]);

    // Check if we need to create a new cart when retailerId changes
    useEffect(() => {
        // TEST HARNESS: never create a cart against the Dutchie worker in Sweed mode.
        if (sweedTest) {
            return;
        }

        // Skip on order confirmation page
        if (isOrderConfirmationPage) {
            return;
        }

        // Don't proceed if we don't have a retailerId
        if (!retailerId) {
            consoleDebug('No retailerId available yet, skipping cart creation');
            return;
        }

        const existingCheckout = getExistingCheckout();

        if (retailerId && (!existingCheckout || existingCheckout.retailerId !== retailerId)) {
            // Clear old checkout if retailer changed
            if (existingCheckout && existingCheckout.retailerId !== retailerId) {
                consoleDebug('🔄 Retailer changed in CartContext, clearing old cart:', {
                    oldRetailer: existingCheckout.retailerId,
                    newRetailer: retailerId
                });
                storage.removeItem(storageItemName);
                setCheckoutData(null);
                // Reset fetch flags to ensure the cart query doesn't use stale data
                fetchAttemptedRef.current = false;
                lastFetchParamsRef.current = null;
            }

            // Create new cart for this retailer
            createCart();
        }
    }, [retailerId, createCart, isOrderConfirmationPage]);

    // Create a stable key for tracking fetch params
    const fetchParamsKey = useMemo(() => {
        if (!retailerId || !checkoutData?.checkoutId || checkoutData?.retailerId !== retailerId) {
            return null;
        }
        return `${checkoutData.checkoutId}-${retailerId}`;
    }, [retailerId, checkoutData?.checkoutId, checkoutData?.retailerId]);

    // Determine if we should skip the query
    const shouldSkip = useMemo(() => {
        // TEST HARNESS: never fetch cart details against the Dutchie worker in Sweed mode.
        if (sweedTest) return true;

        // Always skip on order confirmation page
        if (isOrderConfirmationPage) return true;

        if (!checkoutData || !fetchParamsKey) return true;
        if (lastFetchParamsRef.current === fetchParamsKey && fetchAttemptedRef.current) return true;
        return false;
    }, [checkoutData, fetchParamsKey, isOrderConfirmationPage, sweedTest]);

    const { data, loading, error, refetch, networkStatus, client } = useApolloQuery(
        FETCH_CART_DETAILS,
        {
            variables: {
                checkoutId: checkoutData?.checkoutId,
                retailerId: checkoutData?.retailerId,
            },
            skip: shouldSkip,
            fetchPolicy: 'cache-first',
            nextFetchPolicy: 'cache-first',
            notifyOnNetworkStatusChange: true,
            errorPolicy: 'all',
            onCompleted: (data) => {
                fetchAttemptedRef.current = true;
                lastFetchParamsRef.current = fetchParamsKey;
                consoleDebug('✅ Cart query completed:', {
                    hasCheckout: !!data?.checkout,
                    checkoutId: data?.checkout?.id,
                    itemCount: data?.checkout?.items?.length,
                    pricingType: data?.checkout?.pricingType
                });
            },
            onError: (error) => {
                fetchAttemptedRef.current = true;
                lastFetchParamsRef.current = fetchParamsKey;

                // Only log errors if we're not on order confirmation page
                if (!isOrderConfirmationPage) {
                    console.error('Cart fetch error:', error);

                    if (error.graphQLErrors?.some(err => err.message === 'Not Found')) {
                        console.log('Cart not found, clearing storage and creating new cart');
                        storage.removeItem(storageItemName);
                        setCheckoutData(null);

                        // Clear the cart from cache
                        if (checkoutData?.checkoutId) {
                            client.cache.evict({
                                id: client.cache.identify({
                                    __typename: 'Checkout',
                                    id: checkoutData.checkoutId
                                })
                            });
                            client.cache.gc();
                        }

                        // Create a new cart
                        createCart();
                    }
                }
            }
        }
    );

    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => {
        // TEST HARNESS: expose an inert, "ready" cart so consumers don't block.
        if (sweedTest) {
            return {
                cartData: null,
                isFetched: true,
                isFetching: false,
                isCreatingCart: false,
                error: null,
                refetch: () => {},
                createCart: () => Promise.resolve(),
                retailerId,
                isOrderConfirmationPage,
            };
        }

        return {
            cartData: data?.checkout || null,
            isFetched: shouldSkip || (!loading && !isCreatingCart && (networkStatus === 7 || error)),
            isFetching: loading && !shouldSkip,
            isCreatingCart,
            error: shouldSkip ? null : error,
            refetch: () => {
                fetchAttemptedRef.current = false;
                return refetch();
            },
            createCart,
            retailerId,
            isOrderConfirmationPage // Expose this in case components need to know
        };
    }, [data, loading, isCreatingCart, error, networkStatus, shouldSkip, refetch, createCart, retailerId, isOrderConfirmationPage, sweedTest]);

    return (
        <CartContext.Provider value={contextValue}>
            {children}
        </CartContext.Provider>
    );
};
